import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchBar2 from "../../components/SearchBar2";
import Pagination from "../../components/Pagination";
import { isValidInput } from "../../utils/validation";
import WarnModal from "../../components/WarnModal";
import {
  useDeleteCategoryMutation,
  useGetCategoryMutation,
} from "../../services/main";
import { errorToast, successToast } from "../../helpers";
import Loader from "../../helpers/constants/Loader";
import useTranslation from "../../hooks/Translation";

const ManageCategory = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const [getCategory] = useGetCategoryMutation();
  const [loading, setLoading] = useState(false);
  const [deleteCategory] = useDeleteCategoryMutation();
  const [totalRecords, setTotalRecords] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [categoryData, setCategoryData] = useState([]);

  const getAllCategories = async (page: number) => {
    let body = {
      start: page,
      length: limit,
      search: debouncedSearchTerm,
    };
    setLoading(true);
    try {
      const response = await getCategory(body).unwrap();
      if (response?.status === 200) {
        setLoading(false);
        setCategoryData(response?.data?.response_object);
        setTotalRecords(response?.data?.total_records);
        setTotalPages(Math.ceil(response?.data?.total_records / limit));
      } else {
        setLoading(false);
        setCategoryData([]);
      }
    } catch (error: any) {
      setLoading(false);
      errorToast(error?.data?.message || "");
    }
  };

  const handleDeleteCategory = async (id: any) => {
    try {
      const response = await deleteCategory({ id: id }).unwrap();
      if (response?.status === 200) {
        successToast("Category deleted successfully");
        getAllCategories(page);
        setOpen(false);
      }
    } catch (error: any) {
      console.log(error, "errror");
      errorToast(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getAllCategories(page);
  }, [page, debouncedSearchTerm, limit]);

  return (
    <div>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{t.sideBar.manageCategories}</h1>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <div className="cards_header_right">
              <SearchBar2
                value={searchTerm}
                searchTerm={searchTerm}
                onCross={() => setSearchTerm("")}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </div>

            <Box className="cards_header_right">
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-categories-Main")}
              >
                <AddIcon />
                {t.main.addCategory}
              </Button>
            </Box>
          </Box>

          <TableContainer className="table_container">
            <Table sx={{ minWidth: 650 }} aria-label="sub-category table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">S.No</TableCell>
                  <TableCell>{t.main.image}</TableCell>
                  <TableCell>{t.main.name}</TableCell>
                  <TableCell>{t.main.subCategory}</TableCell>
                  <TableCell>{t.main.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t.error.noSubFound}
                    </TableCell>
                  </TableRow>
                ) : (
                  categoryData?.map((sub: any, index) => (
                    <TableRow key={sub.id}>
                      <TableCell align="center">
                        {(page - 1) * limit + index + 1}
                      </TableCell>
                      <TableCell>
                        <figure className="user_img">
                          <img
                            style={{
                              borderRadius: "50%",
                              width: 50,
                              height: 50,
                            }}
                            src={sub?.category_image?.media_url}
                            alt={sub?.category_image?.media_name}
                          />
                        </figure>
                      </TableCell>
                      <TableCell> {sub?.category_name_en || "-"}</TableCell>
                      <TableCell>
                        <Button
                          className="btn btn_primary"
                          onClick={() =>
                            navigate(
                              `/manage-categories/sub-categories/${sub.id}`,
                              {
                                state: { name: sub?.category_name_en },
                              }
                            )
                          }
                          style={{ marginRight: 20, marginLeft: 20 }}
                        >
                          {t.main.viewSubCategories}
                        </Button>
                      </TableCell>

                      <TableCell>
                        <Box className="table_actions">
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-categories/edit/${sub.id}`)
                            }
                          >
                            <ModeEditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setOpen(true);
                              setSelectedId(sub.id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {categoryData?.length > 0 ? (
          <Pagination
            setPage={setPage}
            module={categoryData}
            page={page}
            onPageChange={setPage}
            totalPages={totalPages}
            setTotalPages={setTotalPages}
            limit={limit}
            setLimit={setLimit}
            totalRecords={totalRecords}
          />
        ) : (
          ""
        )}
      </div>

      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => handleDeleteCategory(selectedId)}
        name={t.main.category}
      />
    </div>
  );
};

export default ManageCategory;
